// API service for making requests to the backend
import { getToken } from './auth';
import { API_URL } from '../config';

// Helper function to handle HTTP errors
const parseJsonBody = async (response) => {
  if (response.status === 204) {
    return null;
  }

  const contentLength = response.headers.get('Content-Length');
  if (contentLength === '0') {
    return null;
  }

  const text = await response.text();
  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch (parseError) {
    console.warn('Failed to parse JSON response:', parseError);
    return null;
  }
};

const handleResponse = async (response) => {
  if (!response.ok) {
    const error = await parseJsonBody(response);

    // Check for detail or error field (generic errors)
    if (error?.detail || error?.error) {
      const detail = error.detail || error.error;
      throw new Error(detail);
    }

    // Check for field validation errors (e.g., {"field": ["error message"]})
    if (error && typeof error === 'object') {
      const fieldErrors = [];
      for (const [field, messages] of Object.entries(error)) {
        if (Array.isArray(messages)) {
          messages.forEach(msg => {
            fieldErrors.push(`${field}: ${msg}`);
          });
        } else if (typeof messages === 'string') {
          fieldErrors.push(`${field}: ${messages}`);
        }
      }

      if (fieldErrors.length > 0) {
        const errorMessage = fieldErrors.join('\n');
        const err = new Error(errorMessage);
        err.fieldErrors = error; // Preserve original field errors for programmatic access
        throw err;
      }
    }

    throw new Error('An unknown error occurred');
  }

  return parseJsonBody(response);
};

// Create request headers with authentication token
const createHeaders = (contentType = 'application/json') => {
  const headers = {};

  if (contentType) {
    headers['Content-Type'] = contentType;
  }
  
  const token = getToken();
  if (!token) {
    console.warn('No authentication token available');
    return headers;
  }

  if (typeof token !== 'string' || token.trim() === '') {
    console.error('Invalid token format');
    return headers;
  }

  headers['Authorization'] = `Bearer ${token}`;
  return headers;
};

// Generic request function
const request = async (endpoint, method = 'GET', data = null, contentType = 'application/json') => {
  const url = `${API_URL}${endpoint}`;
  
  const options = {
    method,
    headers: createHeaders(contentType),
  };
  
  if (data) {
    if (contentType === 'application/json' && typeof data !== 'string') {
      options.body = JSON.stringify(data);
    } else {
      options.body = data;
    }
  }
  
  try {
    const response = await fetch(url, options);
    
    // Handle 401 Unauthorized responses
    if (response.status === 401) {
      const { refreshToken } = await import('./auth');
      try {
        const newToken = await refreshToken();
        if (newToken) {
          options.headers['Authorization'] = `Bearer ${newToken}`;
          const retryResponse = await fetch(url, options);
          return handleResponse(retryResponse);
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        throw new Error('Session expired. Please login again.');
      }
    }
    
    return handleResponse(response);
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
};

// API functions for user management
export const getUserProfile = () => request('/auth/user/');
export const updateUserProfile = (data) => request('/auth/user/', 'PUT', data);
export const updateUserProfileImage = (formData) => request('/auth/user/profile-image/', 'POST', formData, null);
export const checkEmailAvailability = (email) => request(`/auth/check-email/?email=${encodeURIComponent(email)}`);

// API functions for associate member management
export const getAssociateMembers = () => request('/associate-members/');
export const getAssociateMember = (id) => request(`/associate-members/${id}/`);
export const createAssociateMember = (data) => request('/associate-members/', 'POST', data);
export const updateAssociateMember = (id, data) => request(`/associate-members/${id}/`, 'PUT', data);
export const deleteAssociateMember = (id) => request(`/associate-members/${id}/`, 'DELETE');

// API functions for membership management
export const getMembershipTypes = () => request('/membership-types/');
export const getCurrentMembership = () => request('/memberships/active/');
export const getAllMemberships = () => request('/memberships/');
export const renewMembership = (membershipId, data) => request(`/memberships/${membershipId}/renew/`, 'POST', data);
export const purchaseMembership = (data) => request('/memberships/', 'POST', data);
export const updateAutoRenewal = (data) => request(`/memberships/${data.membership_id}/update_auto_renewal/`, 'POST', data);
export const getOnboardingStatus = () => request('/onboarding/');
export const selectOnboardingPlan = (data) => request('/onboarding/select_plan/', 'POST', data);
export const resetOnboarding = () => request('/onboarding/reset/', 'POST', {});

// API functions for events
export const getUpcomingEvents = () => request('/events/upcoming/');
export const getPastEvents = () => request('/events/past/');
export const getEventById = (id) => request(`/events/${id}/`);
export const registerForEvent = (data) => request('/event-registrations/', 'POST', data);
export const cancelEventRegistration = (registrationId) => request(`/event-registrations/${registrationId}/cancel/`, 'POST');
export const adminCancelEventRegistration = (registrationId, note, withholdStripeFee = true) =>
  request(`/event-registrations/${registrationId}/admin_cancel/`, 'POST', {
    note, withhold_stripe_fee: withholdStripeFee,
  });
export const getUserEventRegistrations = () => request('/event-registrations/');
export const getUpcomingRegistrations = () => request('/event-registrations/upcoming/');
export const getEventRegistrations = async (eventId) => {
  // The list endpoint is paginated (PAGE_SIZE=10); follow every page so events
  // with more registrants than one page don't silently drop rows - including
  // cancelled ones - from the admin registrations view.
  let page = 1;
  let allResults = [];
  while (true) {
    const data = await request(`/event-registrations/?event=${eventId}&page=${page}`);
    if (Array.isArray(data)) {
      return data;
    }
    allResults = allResults.concat(data.results || []);
    if (!data.next) {
      return allResults;
    }
    page += 1;
  }
};
export const createEvent = (formData) => request('/events/', 'POST', formData, null);
export const updateEvent = (eventId, formData) => request(`/events/${eventId}/`, 'PATCH', formData, null);
export const deleteEvent = (eventId) => request(`/events/${eventId}/`, 'DELETE');
// Optional: Stripe checkout session creation (backend provides this route)
export const createCheckoutSession = (data) => request('/payments/create-checkout-session/', 'POST', data);

// API functions for payments
export const createPaymentIntent = (data) => request('/payments/create_payment_intent/', 'POST', data);
export const confirmPayment = (data) => request('/payments/confirm_payment/', 'POST', data);
export const createFreeMembership = (data) => request('/payments/create_free_membership/', 'POST', data);
export const checkUpgradeEligibility = (targetMembershipTypeId) => {
  const query = targetMembershipTypeId ? `?target_membership_type_id=${targetMembershipTypeId}` : '';
  return request(`/payments/check_upgrade_eligibility/${query}`);
};
export const getUserPayments = () => request('/payments/');
export const getAllPayments = () => request('/payments/admin/all/');
export const downloadInvoicePDF = async (paymentId) => {
  const token = getToken();
  const response = await fetch(`${API_URL}/payments/${paymentId}/download-invoice/`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to download invoice');
  }

  // Get the blob from the response
  const blob = await response.blob();

  // Create a download link and trigger it
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `invoice_${paymentId}.pdf`;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

// API functions for event payments
export const createEventPaymentIntent = (data) => request('/payments/create_event_payment_intent/', 'POST', data);
export const confirmEventPayment = (data) => request('/payments/confirm_event_payment/', 'POST', data);

// API functions for payment methods management
export const getPaymentMethods = () => request('/payments/payment_methods/');
export const createSetupIntent = () => request('/payments/create_setup_intent/', 'POST');
export const addPaymentMethod = (data) => request('/payments/add_payment_method/', 'POST', data);
export const updateDefaultPaymentMethod = (data) => request('/payments/update_default_payment_method/', 'POST', data);
export const removePaymentMethod = (paymentMethodId) => request(`/payments/remove_payment_method/${paymentMethodId}/`, 'DELETE');

// API functions for coupons
export const validateCoupon = (code, eventId, amount) => request(`/coupons/${code}/validate/`, 'POST', { event_id: eventId, amount });
export const getUserCoupons = () => request('/coupons/');
export const getUserCouponUsage = () => request('/coupon-usage/');

// API functions for credits
export const getUserCredits = () => request('/credits/');
export const getCreditBalance = () => request('/credits/balance/');
export const getActiveCredits = () => request('/credits/active/');
export const getCreditUsageHistory = () => request('/credits/usage_history/');

// Admin user management functions
export const getUsers = () => request('/users/');
export const getUserById = (id) => request(`/users/${id}/`);
export const updateUser = (id, data) => request(`/users/${id}/`, 'PATCH', data);
export const getUserProfileById = (id) => request(`/profiles/${id}/`);
export const updateUserProfileById = (id, data) => request(`/profiles/${id}/`, 'PATCH', data);
export const setUserPassword = (id, password) => request(`/users/${id}/set_password/`, 'POST', { password });
export const generateUserPassword = (id, length = 12) => request(`/users/${id}/generate_password/`, 'POST', { length });
export const sendPasswordResetEmail = (email) => request('/auth/password/reset/', 'POST', { email });

// Support
export const sendSupportContact = (subject, body) => request('/support/contact/', 'POST', { subject, body });
